import {
  BarChart3,
  LineChart,
  PieChart,
  AreaChart,
  Donut,
  Gauge,
  ScatterChart,
} from "lucide-react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { ChartConfigProps } from "./types";
import { DynamicSourceSelector } from "./DynamicSourceSelector";

export function ChartConfig({
  editedComponent,
  updateConfig,
  availableComponents,
  pageComponents,
  dataSourceType,
  setDataSourceType,
}: ChartConfigProps) {
  const dataComponentOptions = availableComponents.filter(
    (c) =>
      c.id !== editedComponent.id &&
      ["table", "list", "kanban"].includes(c.type),
  );

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="chartTitle">Título</Label>
        <Input
          id="chartTitle"
          type="text"
          value={editedComponent.config.title || ""}
          onChange={(e) => updateConfig("title", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label>Tipo de Gráfico</Label>
        <div className="grid grid-cols-8 gap-2 pt-2">
          {[
            { value: "pie", label: "Pizza", Icon: PieChart },
            { value: "bar", label: "Barras", Icon: BarChart3 },
            { value: "line", label: "Linha", Icon: LineChart },
            { value: "area", label: "Área", Icon: AreaChart },
            { value: "donut", label: "Rosca", Icon: Donut },
            { value: "radialBar", label: "Radial", Icon: PieChart },
            { value: "gauge", label: "Medidor", Icon: Gauge },
            { value: "scatter", label: "Dispersão", Icon: ScatterChart },
          ].map((chartType) => {
            const isSelected =
              editedComponent.config.chartType === chartType.value;
            return (
              <button
                key={chartType.value}
                type="button"
                onClick={() => updateConfig("chartType", chartType.value)}
                className={`group relative flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl border transition-all duration-200 ${
                  isSelected
                    ? "border-primary bg-primary/5 shadow-md scale-105"
                    : "border-border bg-card hover:border-primary/50 hover:bg-accent hover:scale-105"
                }`}
              >
                <chartType.Icon
                  className={`w-4 h-4 ${isSelected ? "text-primary" : "text-muted-foreground group-hover:text-foreground"}`}
                />
                <span className="text-[9px] font-medium text-center leading-tight">
                  {chartType.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="dataSourceType">Fonte de Dados</Label>
        <Select
          value={dataSourceType === "googleSheets" ? "manual" : dataSourceType}
          onValueChange={(value: "manual" | "component" | "dynamic") => {
            setDataSourceType(value);
            if (value === "component") {
              updateConfig("sourceComponentId", "");
              updateConfig("dynamicSourceField", undefined);
              updateConfig("googleSheets", undefined);
            } else if (value === "dynamic") {
              updateConfig("dynamicSourceField", editedComponent.config.dynamicSourceField || "");
              updateConfig("sourceComponentId", undefined);
              updateConfig("googleSheets", undefined);
            } else {
              updateConfig("sourceComponentId", undefined);
              updateConfig("dynamicSourceField", undefined);
              updateConfig("googleSheets", undefined);
            }
          }}
        >
          <SelectTrigger id="dataSourceType">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Dados Manuais</SelectItem>
            <SelectItem value="component">
              Usar dados de outro componente
            </SelectItem>
            <SelectItem value="dynamic">Dinâmica (via filtro)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {dataSourceType === "dynamic" && (
        <DynamicSourceSelector
          pageComponents={pageComponents}
          value={editedComponent.config.dynamicSourceField}
          onChange={(field) => updateConfig("dynamicSourceField", field)}
        />
      )}

      {dataSourceType === "component" && (
        <div className="space-y-2">
          <Label htmlFor="sourceComponent">Componente de Origem</Label>
          <Select
            value={editedComponent.config.sourceComponentId || ""}
            onValueChange={(value) =>
              updateConfig("sourceComponentId", value)
            }
          >
            <SelectTrigger id="sourceComponent">
              <SelectValue placeholder="Selecione um componente" />
            </SelectTrigger>
            <SelectContent>
              {dataComponentOptions.map((comp) => {
                const pageName = (comp as any)._pageName;
                const displayName = pageName
                  ? `${comp.config.title || comp.type} (Página: ${pageName})`
                  : `${comp.config.title || comp.type} (${comp.type})`;
                return (
                  <SelectItem key={comp.id} value={comp.id}>
                    {displayName}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {dataComponentOptions.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhuma tabela ou lista disponível no workspace
            </p>
          )}
        </div>
      )}
    </>
  );
}
